import type { DateFilterModel } from "ag-grid-community";
import { type CustomFilterProps, useGridFilter } from "ag-grid-react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Color } from "src/elements";
import { toPostgresDateString } from "src/pages/Merchflows/store/agGridModelMapper";

type Props = CustomFilterProps<unknown, unknown, DateFilterModel>;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
  padding: 8px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
`;

const Input = styled.input`
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  padding: 6px 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: end;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  background: ${Color.white};
  padding: 4px 10px;
  cursor: pointer;
`;

const toDraftDateValue = (value: string | null | undefined) => toPostgresDateString(value) || "";

const getDraftDatesFromModel = (model: DateFilterModel | null | undefined) => ({
  from: toDraftDateValue(model?.dateFrom),
  to: toDraftDateValue(model?.dateTo),
});

const modelToDisplayValue = (model: DateFilterModel | null | undefined) => {
  const draftDates = getDraftDatesFromModel(model);

  if (!draftDates.from && !draftDates.to) {
    return "";
  }

  if (draftDates.from === draftDates.to) {
    return draftDates.from;
  }

  return `${draftDates.from} - ${draftDates.to}`;
};

export const BackendDateRangeFilter = ({ model, onModelChange }: Props) => {
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");

  useEffect(() => {
    const draftDates = getDraftDatesFromModel(model);
    setDraftFrom(draftDates.from);
    setDraftTo(draftDates.to);
  }, [model]);

  useGridFilter({
    doesFilterPass: () => true,
    getModelAsString: (currentModel) => modelToDisplayValue(currentModel as DateFilterModel | null),
  });

  const apply = () => {
    const normalizedFrom = draftFrom || draftTo;
    const normalizedTo = draftTo || draftFrom;

    if (!normalizedFrom && !normalizedTo) {
      onModelChange(null);
      return;
    }

    onModelChange({
      filterType: "date",
      type: "inRange",
      dateFrom: `${normalizedFrom} 00:00:00`,
      dateTo: `${normalizedTo} 00:00:00`,
    });
  };

  const clear = () => {
    setDraftFrom("");
    setDraftTo("");
    onModelChange(null);
  };

  return (
    <Container>
      <Label>
        From
        <Input
          type="date"
          value={draftFrom}
          onChange={(event) => setDraftFrom(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              apply();
            }
          }}
        />
      </Label>

      <Label>
        To
        <Input
          type="date"
          value={draftTo}
          onChange={(event) => setDraftTo(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              apply();
            }
          }}
        />
      </Label>

      <Actions>
        <ActionButton type="button" onClick={clear}>
          Clear
        </ActionButton>

        <ActionButton type="button" onClick={apply}>
          Apply
        </ActionButton>
      </Actions>
    </Container>
  );
};
